import { BrainIcon, CalendarIcon, LeafIcon, ZapIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { i18n } from '@/shared/i18n';
import type { DurationTypeValue, InsightType } from './types';

export const DURATION_TYPE_VALUES: DurationTypeValue[] = [
  'flash',
  'seasonal',
  'behavioral',
  'evergreen',
];

// A factory rather than a table of getters: consumers build filter options out of this map,
// and any read that happens while a module is evaluated would freeze one language in.
export const buildDurationTypeLabels = (): Record<DurationTypeValue, string> => ({
  flash: i18n.t('analysis:socialMediaOpportunitiesWidget.durationTypeConfig.flash'),
  seasonal: i18n.t('analysis:socialMediaOpportunitiesWidget.durationTypeConfig.seasonal'),
  behavioral: i18n.t('analysis:socialMediaOpportunitiesWidget.durationTypeConfig.behavioral'),
  evergreen: i18n.t('analysis:socialMediaOpportunitiesWidget.durationTypeConfig.evergreen'),
});

/**
 * Each pair is a design-system colour stop, named in the trailing comment.
 *
 * They stay hex rather than `var()` references because the package exposes the
 * colour scale neither as CSS custom properties nor through a runtime export,
 * so there is nothing to point at from a call site. Naming the stop is what
 * makes a palette change greppable in the meantime.
 */
export const OPPORTUNITY_TYPE_CONFIG: Record<
  DurationTypeValue,
  {
    bg: string;
    text: string;
    /** Icon tile on the detail card: the text colour as fill, the bg colour as glyph. */
    tileBg: string;
    tileIcon: string;
    Icon: ComponentType<LucideProps>;
  }
> = {
  // --red-100 / --red-500; dark follows the badge-red pair
  flash: {
    bg: 'bg-[#ffebf0] dark:bg-badge-red',
    text: 'text-[#ff527d] dark:text-badge-red-foreground',
    tileBg: 'bg-[#ff527d] dark:bg-badge-red-foreground',
    tileIcon: 'text-[#ffebf0] dark:text-badge-red',
    Icon: ZapIcon,
  },
  // --orange-100 / --orange-900
  seasonal: {
    bg: 'bg-[#feeedc] dark:bg-badge-orange',
    text: 'text-[#784102] dark:text-badge-orange-foreground',
    tileBg: 'bg-[#784102] dark:bg-badge-orange-foreground',
    tileIcon: 'text-[#feeedc] dark:text-badge-orange',
    Icon: CalendarIcon,
  },
  // --purple-100 / --purple-900
  behavioral: {
    bg: 'bg-[#f3eafb] dark:bg-badge-purple',
    text: 'text-[#501881] dark:text-badge-purple-foreground',
    tileBg: 'bg-[#501881] dark:bg-badge-purple-foreground',
    tileIcon: 'text-[#f3eafb] dark:text-badge-purple',
    Icon: BrainIcon,
  },
  // --stone-100 / --stone-800
  evergreen: {
    bg: 'bg-[#f5f5f4] dark:bg-secondary',
    text: 'text-[#292524] dark:text-secondary-foreground',
    tileBg: 'bg-[#292524] dark:bg-secondary-foreground',
    tileIcon: 'text-[#f5f5f4] dark:text-secondary',
    Icon: LeafIcon,
  },
};

// Copy is fixed by design here, not supplied by the API.
export const OPPORTUNITY_TYPE_INFO: Record<
  DurationTypeValue,
  { description: string; tags: string[] }
> = {
  flash: {
    get description() {
      return i18n.t('analysis:opportunity.durationType.flash.description');
    },
    get tags() {
      return [
        i18n.t('analysis:opportunity.durationType.flash.tags.viralMoment'),
        i18n.t('analysis:opportunity.durationType.flash.tags.meme'),
        i18n.t('analysis:opportunity.durationType.flash.tags.newsHook'),
      ];
    },
  },
  seasonal: {
    get description() {
      return i18n.t('analysis:opportunity.durationType.seasonal.description');
    },
    get tags() {
      return [
        i18n.t('analysis:opportunity.durationType.seasonal.tags.culturalMoment'),
        i18n.t('analysis:opportunity.durationType.seasonal.tags.quarterlyCycle'),
        i18n.t('analysis:opportunity.durationType.seasonal.tags.eventDriven'),
      ];
    },
  },
  behavioral: {
    get description() {
      return i18n.t('analysis:opportunity.durationType.behavioral.description');
    },
    get tags() {
      return [
        i18n.t('analysis:opportunity.durationType.behavioral.tags.audienceHabit'),
        i18n.t('analysis:opportunity.durationType.behavioral.tags.channelShift'),
        i18n.t('analysis:opportunity.durationType.behavioral.tags.emergingExpectation'),
      ];
    },
  },
  evergreen: {
    get description() {
      return i18n.t('analysis:opportunity.durationType.evergreen.description');
    },
    get tags() {
      return [
        i18n.t('analysis:opportunity.durationType.evergreen.tags.fundamentalNeed'),
        i18n.t('analysis:opportunity.durationType.evergreen.tags.coreBehavior'),
        i18n.t('analysis:opportunity.durationType.evergreen.tags.categoryTruth'),
      ];
    },
  },
};

export const INSIGHT_TYPE_CONFIG: Record<InsightType, { bg: string }> = {
  social: { bg: 'bg-[#a30029]' },
  performance: { bg: 'bg-[#067445]' },
  competition: { bg: 'bg-[#784102]' },
  brand_product: { bg: 'bg-[#631e9f]' },
  strategic: { bg: 'bg-[#31868c]' },
};

// A factory for the same reason as the duration labels: the toolbar's filter options are
// derived from this map, so a getter read while that module evaluates would freeze English.
export const buildInsightTypeLabels = (): Record<InsightType, string> => ({
  social: i18n.t('analysis:opportunity.insightType.social'),
  performance: i18n.t('analysis:opportunity.insightType.performance'),
  competition: i18n.t('analysis:opportunity.insightType.competition'),
  brand_product: i18n.t('analysis:opportunity.insightType.brandProduct'),
  strategic: i18n.t('analysis:opportunity.insightType.strategic'),
});
